import { useEffect, useState } from "react";
import { resolveListingImages } from "@/lib/categoryImages";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Store, MapPin, Package, PoundSterling, PlusCircle,
  CheckCircle2, ClipboardCopy, Send, Pencil, ShieldCheck, Timer,
} from "lucide-react";
import { api, formatApiError, gbp, floorLabel } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

/**
 * Operational retailer management page. Admin's landing zone for every
 * retailer: profile, stock KPIs, inventory list with per-row actions, and
 * the claim-account workflow for admin-created shops.
 */
export default function AdminRetailerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [claimLink, setClaimLink] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/admin/retailers/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function generateClaimLink() {
    try {
      const r = await api.post(`/admin/retailers/${id}/claim-invite`);
      const path = r.data.claim_path || `/claim/${r.data.claim_token}`;
      const fullUrl = `${window.location.origin}${path}`;
      setClaimLink(fullUrl);
      toast.success("Invitation link generated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function copyClaimLink() {
    if (!claimLink) return;
    try {
      await navigator.clipboard.writeText(claimLink);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  }

  async function markSold(listingId) {
    if (!window.confirm("Mark this remnant as sold?")) return;
    await api.post(`/retailer/listings/${listingId}/mark-sold`);
    toast.success("Marked sold");
    load();
  }
  async function markAvailable(listingId) {
    await api.post(`/retailer/listings/${listingId}/mark-available`);
    toast.success("Marked available");
    load();
  }
  async function confirmAvailable(listingId) {
    await api.post(`/retailer/listings/${listingId}/confirm-available`);
    toast.success("Confirmed as still available");
    load();
  }

  async function toggleFounder() {
    const on = !data.retailer.is_founder;
    try {
      await api.post(`/admin/retailers/${id}/founder?on=${on}`);
      toast.success(on ? "Marked as founder — 0% commission" : "Founder status removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function doStatusAction(action) {
    if (action === "pause" || action === "suspend") {
      let msg = `${action === "pause" ? "Pause" : "Suspend"} ${data.retailer.name}?`;
      try {
        const c = await api.get(`/admin/retailers/${id}/commitments`);
        const d = c.data;
        msg += `\n\nOutstanding commitments:\n· ${d.live_listings} live listings (will be hidden from search)\n· ${d.active_reservations} active reservations (${d.paid_awaiting_collection} paid, awaiting collection)\n· ${d.open_enquiries} open enquiries\n\nPaid orders stay accessible to customers via their links.`;
        if (action === "pause") msg += "\nThe retailer can still sign in and manage existing orders.";
        else msg += "\nThe retailer will be signed out and fully blocked.";
      } catch (e) { /* show basic confirm if commitments fetch fails */ }
      if (!window.confirm(msg)) return;
    }
    try {
      await api.post(`/admin/retailers/${id}/${action}`);
      toast.success(action === "pause" ? "Retailer paused" : action === "suspend" ? "Retailer suspended" : "Retailer reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading) return <RetailerLayout><div className="p-6 text-slate-500">Loading…</div></RetailerLayout>;
  if (!data) return <RetailerLayout><div className="p-6 text-slate-500">Retailer not found.</div></RetailerLayout>;

  const r = data.retailer;
  const counts = data.counts;

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6" data-testid="admin-retailer-detail">
        <button
          onClick={() => nav("/admin")}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#E05915] mb-3"
          data-testid="back-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Back to overview
        </button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <span className="grid place-items-center w-12 h-12 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B] shrink-0">
              <Store className="w-6 h-6" />
            </span>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-2xl text-slate-900 truncate" data-testid="retailer-name">
                {r.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 mt-0.5">
                {r.district && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {r.district}
                  </span>
                )}
                {r.postcode && <span className="font-mono text-xs">{r.postcode}</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Chip on={r.is_verified} icon={<ShieldCheck className="w-3 h-3" />}>Verified</Chip>
                <Chip on={r.is_claimed}>
                  {r.is_claimed ? "Account claimed" : "Unclaimed"}
                </Chip>
                {r.is_demo && <Chip on className="bg-slate-100 text-slate-600 border-slate-200">Demo</Chip>}
                {(r.account_status || "active") === "paused" && <Chip on className="bg-amber-100 text-amber-800 border-amber-200">Paused</Chip>}
                {(r.account_status || "active") === "suspended" && <Chip on className="bg-red-100 text-red-800 border-red-200">Suspended</Chip>}
                {r.is_founder && <Chip on className="bg-[#E05915]/10 text-[#E05915] border-[#E05915]/20">Founder · 0% fee</Chip>}
                {r.stripe_charges_enabled && <Chip on className="bg-emerald-100 text-emerald-800 border-emerald-200">Payouts ready</Chip>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to={`/retailer/new?retailer_id=${id}`}
              className="fm-btn-primary inline-flex items-center gap-2"
              data-testid="add-stock-btn"
            >
              <PlusCircle className="w-4 h-4" /> Add stock
            </Link>
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-700 text-sm"
              data-testid="edit-retailer-btn"
            >
              <Pencil className="w-4 h-4" /> Edit retailer
            </button>
            <button
              type="button"
              onClick={toggleFounder}
              className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border font-semibold text-sm ${r.is_founder ? "border-[#E05915] text-[#E05915] bg-[#E05915]/5" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="toggle-founder-btn"
            >
              {r.is_founder ? "Founder ✓ (0%)" : "Make founder"}
            </button>
            {(r.account_status || "active") === "active" ? (
              <button type="button" onClick={() => doStatusAction("pause")}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-amber-300 text-amber-800 bg-white hover:bg-amber-50 font-semibold text-sm"
                data-testid="pause-retailer-btn">Pause</button>
            ) : (
              <button type="button" onClick={() => doStatusAction("reactivate")}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-semibold text-sm"
                data-testid="reactivate-retailer-btn">Reactivate</button>
            )}
            {(r.account_status || "active") !== "suspended" && (
              <button type="button" onClick={() => doStatusAction("suspend")}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-red-300 text-red-700 bg-white hover:bg-red-50 font-semibold text-sm"
                data-testid="suspend-retailer-btn">Suspend</button>
            )}
            {!r.is_claimed && (
              <button
                type="button"
                onClick={generateClaimLink}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-[#2A5A3B] text-[#2A5A3B] hover:bg-[#2A5A3B]/5 font-semibold text-sm"
                data-testid="invite-claim-btn"
              >
                <Send className="w-4 h-4" /> Invite / Claim
              </button>
            )}
          </div>
        </div>

        {claimLink && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="claim-link-panel">
            <div className="text-sm font-semibold text-emerald-900 mb-1">Send this link to the retailer</div>
            <div className="text-xs text-emerald-700 mb-2">
              Single-use · expires in 7 days · they can set their own email and password.
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={claimLink}
                className="fm-input font-mono text-xs bg-white"
                data-testid="claim-link-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copyClaimLink}
                className="inline-flex items-center gap-1 text-sm font-semibold px-3 rounded-md bg-white border border-emerald-300 hover:bg-emerald-50"
                data-testid="claim-copy-btn"
              >
                <ClipboardCopy className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          <Kpi icon={<Package className="w-4 h-4" />} label="Live" value={counts.live} testId="rk-live" />
          <Kpi icon={<Timer className="w-4 h-4" />} label="Reserved" value={counts.reserved} testId="rk-reserved" />
          <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="Sold" value={counts.sold} testId="rk-sold" />
          <Kpi icon={<Package className="w-4 h-4" />} label="Hidden / expired" value={counts.hidden + counts.expired} testId="rk-hidden" />
          <Kpi icon={<PoundSterling className="w-4 h-4" />} label="Gross sales" value={gbp(data.gross_sales_gbp)} testId="rk-gross" />
        </div>

        {/* Contact block (admin-only, PII) */}
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="font-display font-semibold text-slate-900 mb-2">Contact & address <span className="text-xs font-normal text-slate-500">· admin only, never public</span></h2>
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <Row label="Contact name" value={r.contact_name} />
            <Row label="Phone" value={r.phone} />
            <Row label="Email" value={r.email} />
            <Row label="Website" value={r.website} />
            <Row label="Address" value={r.address} />
            <Row label="City" value={r.city} />
            <Row label="County" value={r.county} />
            <Row label="Postcode" value={r.postcode} />
          </div>
        </div>

        {/* Inventory */}
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold text-slate-900">
              Inventory <span className="text-slate-500 text-sm font-normal">({data.total_listings})</span>
            </h2>
            <Link to={`/retailer/new?retailer_id=${id}`} className="text-sm font-semibold text-[#E05915]">
              + Add stock
            </Link>
          </div>
          {data.listings.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No listings yet — tap Add stock.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="admin-inventory-list">
              {data.listings.map((l) => (
                <li key={l.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {resolveListingImages(l).photos[0] && (
                      <img src={resolveListingImages(l).photos[0]} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
                    )}
                    <div className="min-w-0">
                      <div className="text-[11px] font-mono text-slate-500">{l.reference_code}</div>
                      <div className="font-semibold text-slate-900 truncate capitalize">
                        {l.colour} {floorLabel(l.flooring_type)}
                      </div>
                      <div className="text-xs text-slate-600">
                        {l.width_m}m × {l.length_m}m · {gbp(l.price_gbp)}
                      </div>
                    </div>
                  </div>
                  <StatusPill status={l.status} />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Link
                      to={`/retailer/listings/${l.id}/edit`}
                      className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                      data-testid={`admin-edit-${l.reference_code}`}
                    >
                      Edit
                    </Link>
                    <RowBtn onClick={() => confirmAvailable(l.id)} testId={`admin-confirm-${l.id}`}>Still available</RowBtn>
                    {l.status === "live" && <RowBtn onClick={() => markSold(l.id)} testId={`admin-sold-${l.id}`}>Mark sold</RowBtn>}
                    {(l.status === "sold" || l.status === "reserved") && (
                      <RowBtn onClick={() => markAvailable(l.id)} testId={`admin-available-${l.id}`}>Mark available</RowBtn>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {showEdit && (
          <EditRetailerModal
            retailer={r}
            onClose={() => setShowEdit(false)}
            onSaved={() => { setShowEdit(false); load(); }}
          />
        )}
      </main>
    </RetailerLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 min-w-[110px]">{label}:</span>
      <span className="text-slate-900 break-all">{value || <em className="text-slate-400 font-normal">—</em>}</span>
    </div>
  );
}
function Kpi({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
function Chip({ on, icon, children, className = "" }) {
  if (!on) return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-500 rounded-full px-2 py-1 border border-slate-200">
      {icon}{children}
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1 border ${className || "bg-[#2A5A3B]/10 text-[#2A5A3B] border-[#2A5A3B]/20"}`}>
      {icon}{children}
    </span>
  );
}
function StatusPill({ status }) {
  const map = {
    live: "bg-emerald-100 text-emerald-800",
    reserved: "bg-amber-100 text-amber-800",
    sold: "bg-slate-200 text-slate-700",
    expired: "bg-red-100 text-red-800",
    hidden: "bg-slate-100 text-slate-500",
    draft: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100"}`}>
      {status}
    </span>
  );
}
function RowBtn({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function EditRetailerModal({ retailer, onClose, onSaved }) {
  const [form, setForm] = useState(retailer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      const fields = [
        "name","contact_name","phone","email","website","address","city",
        "county","postcode","description","is_verified","is_active","is_demo",
        "collection_available","delivery_available","measuring_available",
        "carpet_fitting","vinyl_fitting","laminate_fitting","lvt_fitting",
        "old_flooring_removal",
      ];
      fields.forEach((k) => { if (form[k] !== undefined) payload[k] = form[k]; });
      await api.patch(`/admin/retailers/${retailer.id}`, payload);
      toast.success("Retailer saved");
      onSaved();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="edit-retailer-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl border border-slate-200 max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-semibold text-xl text-slate-900">Edit retailer</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-edit-retailer">✕</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Business name" val={form.name} onChange={(v) => set("name", v)} testId="er-name" />
            <F label="Contact name" val={form.contact_name} onChange={(v) => set("contact_name", v)} testId="er-contact" />
            <F label="Phone" val={form.phone} onChange={(v) => set("phone", v)} testId="er-phone" />
            <F label="Email" val={form.email} onChange={(v) => set("email", v)} testId="er-email" type="email" />
            <F label="Website" val={form.website} onChange={(v) => set("website", v)} testId="er-website" />
            <F label="Postcode" val={form.postcode} onChange={(v) => set("postcode", (v || "").toUpperCase())} testId="er-postcode" />
            <F label="Address" val={form.address} onChange={(v) => set("address", v)} testId="er-address" span2 />
            <F label="City" val={form.city} onChange={(v) => set("city", v)} testId="er-city" />
            <F label="County" val={form.county} onChange={(v) => set("county", v)} testId="er-county" />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Toggle label="Verified" on={form.is_verified} onChange={(v) => set("is_verified", v)} testId="er-verified" />
            <Toggle label="Active" on={form.is_active !== false} onChange={(v) => set("is_active", v)} testId="er-active" />
            <Toggle label="Collection" on={form.collection_available !== false} onChange={(v) => set("collection_available", v)} testId="er-collection" />
            <Toggle label="Fitting (carpet)" on={form.carpet_fitting} onChange={(v) => set("carpet_fitting", v)} testId="er-cfit" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-11 px-4 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-700">Cancel</button>
            <button type="submit" disabled={saving} className="fm-btn-primary disabled:opacity-70" data-testid="er-save">
              {saving ? "Saving…" : "Save retailer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
function F({ label, val, onChange, testId, span2, type = "text" }) {
  return (
    <label className={span2 ? "sm:col-span-2" : ""}>
      <span className="fm-label">{label}</span>
      <input type={type} value={val || ""} onChange={(e) => onChange(e.target.value)} className="fm-input" data-testid={testId} />
    </label>
  );
}
function Toggle({ label, on, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md px-3 py-2 cursor-pointer">
      <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} className="accent-[#2A5A3B]" data-testid={testId} />
      {label}
    </label>
  );
}
